// @givmo/app-admin — combined Invite / Edit admin dialog (07-1.5b).
//
// One dialog, mode driven by the `adminToEdit` prop (ports the Flutter `EditOrInviteAdminPopUp`):
//   - INVITE (adminToEdit == null): title "Invite new admin", submit "Invite admin", shows the
//     "Work email" field, default role = Owner. On submit fires `companiesCreateCompanyAdmin`
//     (POST /admin/company/admins) with a `CompanyAdminRegister` body { first_name, last_name, email, role }.
//   - EDIT (adminToEdit set): title "Edit admin", submit "Save", email HIDDEN, names pre-filled,
//     default role = the admin's current role. Save = a TWO-CALL fan-out: ALWAYS the name PUT
//     (`companiesUpdateCompanyAdminName`), then the role PUT (`companiesUpdateCompanyAdminRole`) ONLY
//     when the role actually changed from the admin's current role (no-op suppression prevents an
//     accidental downgrade — Flutter parity).
//
// Validation (verbatim copy): names match `^[a-zA-Z]+$` → "Please enter a valid name using only
// letters"; email format → "Invalid email format". Invite-only client domain enforcement: if the
// company has a non-empty `domain`, the email's domain must match (case-insensitively) or the submit
// is blocked with "Email domain must match company domain (<domain>)". A server 422/403 is surfaced
// inline too.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sdk,
  isGivmoError,
  type AdminOut,
  type CompanyAdminMembershipType,
  type CompanyAdminRegister,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  Field,
  RadioTile,
  RadioTileGroup,
  testIdProp,
} from '@givmo/design-system';
import {
  EMAIL_REGEX,
  INVALID_EMAIL_MESSAGE,
  NAME_REGEX,
  INVALID_NAME_MESSAGE,
} from '../auth/auth-schemas';
import { ROLE_OWNER, ROLE_MEMBER, OWNER_DESC, MEMBER_DESC, normalizeRole } from './admins-roles';

export interface AdminEditInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → invite mode; an admin → edit mode. */
  adminToEdit: AdminOut | null;
  /** The company domain (CompanyDetailedOut.domain); '' disables the invite domain check. */
  companyDomain: string;
  /** The query key to invalidate on success (the admins infinite list). */
  invalidateKey: readonly unknown[];
}

export function AdminEditInviteDialog({
  open,
  onOpenChange,
  adminToEdit,
  companyDomain,
  invalidateKey,
}: AdminEditInviteDialogProps) {
  const queryClient = useQueryClient();
  const isInvite = adminToEdit === null;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CompanyAdminMembershipType>(ROLE_OWNER);

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // (Re)prime the fields whenever the dialog opens OR the target admin changes (`adminToEdit` is the
  // page's stable `dialog.admin` ref, so this re-primes on a real target switch without spurious runs —
  // including reusing the dialog for a different admin without closing). Invite defaults to Owner +
  // blank fields; edit pre-fills the names + the admin's current role.
  useEffect(() => {
    if (!open) return;
    setFirstName(adminToEdit?.first_name ?? '');
    setLastName(adminToEdit?.last_name ?? '');
    setEmail('');
    setRole(normalizeRole(adminToEdit?.admin_company_role));
    setNameError(false);
    setEmailError(null);
    setServerError(null);
  }, [open, adminToEdit]);

  const invite = useMutation({
    mutationFn: (body: CompanyAdminRegister) =>
      sdk.companiesCreateCompanyAdmin({ body, throwOnError: true }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidateKey });
      onOpenChange(false);
    },
    onError: (error: unknown) => setServerError(serverMessage(error)),
  });

  const edit = useMutation({
    // Edit fan-out: name PUT ALWAYS; role PUT ONLY when the role changed. Name first, then role —
    // so a name success is durable even if the role call fails (partial failure is surfaced, not
    // swallowed). `roleChanged` is computed at call time against the admin's current role.
    mutationFn: async (args: {
      id: string;
      firstName: string;
      lastName: string;
      role: CompanyAdminMembershipType;
      roleChanged: boolean;
    }) => {
      await sdk.companiesUpdateCompanyAdminName({
        path: { target_user_id: args.id },
        body: { new_first_name: args.firstName, new_last_name: args.lastName },
        throwOnError: true,
      });
      if (args.roleChanged) {
        await sdk.companiesUpdateCompanyAdminRole({
          path: { target_user_id: args.id },
          body: { new_role: args.role },
          throwOnError: true,
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidateKey });
      onOpenChange(false);
    },
    onError: (error: unknown) => setServerError(serverMessage(error)),
  });

  const submitting = invite.isPending || edit.isPending;

  // The submit button is active only when both names are filled (and, in invite mode, the email).
  const buttonActive =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (!isInvite || email.trim().length > 0);

  const onSubmit = () => {
    setNameError(false);
    setEmailError(null);
    setServerError(null);

    const namesValid = NAME_REGEX.test(firstName) && NAME_REGEX.test(lastName);
    if (!namesValid) setNameError(true);

    let emailValid = true;
    if (isInvite) {
      if (!EMAIL_REGEX.test(email)) {
        setEmailError(INVALID_EMAIL_MESSAGE);
        emailValid = false;
      } else if (companyDomain.length > 0) {
        const emailDomain = email.split('@').pop()?.toLowerCase();
        if (emailDomain !== companyDomain.toLowerCase()) {
          setEmailError(`Email domain must match company domain (${companyDomain})`);
          emailValid = false;
        }
      }
    }

    if (!namesValid || !emailValid) return;

    if (isInvite) {
      invite.mutate({ first_name: firstName, last_name: lastName, email, role });
    } else {
      const id = adminToEdit.id;
      const roleChanged = normalizeRole(adminToEdit.admin_company_role) !== role;
      edit.mutate({ id, firstName, lastName, role, roleChanged });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label={isInvite ? 'Invite new admin' : 'Edit admin'}
        className="flex flex-col gap-16"
        testId="admins-edit-invite-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isInvite ? 'Invite new admin' : 'Edit admin'}
        </Dialog.Title>

        <div className="flex gap-16">
          <div className="flex-1">
            <Field
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              testId="invite-firstname"
            />
          </div>
          <div className="flex-1">
            <Field
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              testId="invite-lastname"
            />
          </div>
        </div>
        {nameError && (
          <ErrorMessage testId="admins-name-error">{INVALID_NAME_MESSAGE}</ErrorMessage>
        )}

        {isInvite && (
          <Field
            label="Work email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            testId="invite-email"
          />
        )}
        {emailError !== null && (
          <ErrorMessage testId="admins-email-error">{emailError}</ErrorMessage>
        )}

        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          Choose role
        </span>
        <RadioTileGroup
          value={role}
          onValueChange={(next) => setRole(next as CompanyAdminMembershipType)}
          aria-label="Choose role"
          className="gap-12"
          testId="admins-role-group"
        >
          <RadioTile
            value={ROLE_OWNER}
            label={ROLE_OWNER}
            description={OWNER_DESC}
            testId={isInvite ? 'invite-role-owner' : 'edit-role-owner'}
          />
          <RadioTile
            value={ROLE_MEMBER}
            label={ROLE_MEMBER}
            description={MEMBER_DESC}
            testId={isInvite ? 'invite-role-member' : 'edit-role-member'}
          />
        </RadioTileGroup>

        {serverError !== null && (
          <ErrorMessage testId="admins-edit-invite-error">{serverError}</ErrorMessage>
        )}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={submitting}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp('admins-edit-invite-cancel')}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={submitting}
            disabled={!buttonActive}
            onClick={onSubmit}
            testId="admins-edit-invite-submit"
          >
            {isInvite ? 'Invite admin' : 'Save'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function serverMessage(error: unknown): string {
  return isGivmoError(error) ? error.message : 'Something went wrong. Please try again.';
}
